import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { getCR, saveCR, getDossier } from '../services/api';
import BarcodeScanner from '../components/BarcodeScanner';
import { saveDraft, loadDraft, removeDraft, saveDraftSync } from '../services/draftStorage';
import { preparerPhoto, poserAdresseCommande, oublierLieu } from '../services/photo';
import { messageEnvoi, poidsApprox } from '../services/erreurEnvoi';

// PTO_C est exclu : un devis SAV facture un tirage, et la pose d'une prise
// existante n'en est pas un.
const TYPES_RACCORDEMENT = ['RAC_I', 'RAC_C', 'RAC_F', 'RAC_P'];

// Le forfait plus-value, comme en production : il ne vaut que pour un
// raccordement suite SAV — une vraie pose de cable — et se deduit alors de la
// longueur tiree, jamais d'une case cochee sur le terrain.
const SEUILS_FPV = { RAC_I: 150, RAC_CPF: 300 };
function familleFpv(typo) {
  const x = String(typo || '').trim().toUpperCase();
  if (/RAC_I|REPRO_I/.test(x)) return 'RAC_I';
  if (/RAC_C|RAC_P|RAC_F|REPRO_C|REPRO_P|REPRO_F/.test(x)) return 'RAC_CPF';
  return null;
}

const TYPES_REPARATION = [
  'Jarretière PM',
  'Jarretière PTO',
  'Fibre HS (PTO / PB / PM)',
  'Reconstruction',
  'Reprise complète du câblage',
  'Changement ONT',
];

const TYPES_ECHEC_SAV = [
  'Client absent',
  'Problème de continuité PTO / PBO',
  'Problème de continuité PBO / PM',
  'PBO vandalisé',
  'PM vandalisé',
  'Problème signal coupleur',
];

// Une rupture entre la PTO et le PBO se répare en tirant un nouveau câble :
// ce motif appelle toujours un devis, et le devis n'est chiffrable qu'avec
// les longueurs de câble.
const ECHEC_DEVIS_OBLIGATOIRE = 'Problème de continuité PTO / PBO';

/**
 * La photo est reduite, puis horodatee et situee.
 *
 * Covage exige la date, l'heure et l'adresse en clair sur l'image. Le bandeau
 * est dessine par services/photo.js, qui connait deja l'adresse de la commande
 * et s'en sert quand le GPS ne repond pas.
 */
function compressImage(dataUrl) {
  return preparerPhoto(dataUrl);
}

const PhotoSection = React.memo(function PhotoSection({ label, photos, onChange, max = 0 }) {
  const [compressing, setCompressing] = useState(false);
  const handleAdd = async e => {
    const files = Array.from(e.target.files);
    e.target.value = '';
    if (!files.length) return;
    setCompressing(true);
    try {
      for (const file of files) {
        await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = async ev => {
            try {
              const compressed = await compressImage(ev.target.result);
              onChange(prev => [...prev, compressed]);
            } catch {}
            resolve();
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }
    } finally {
      setCompressing(false);
    }
  };
  const remove = i => onChange(photos.filter((_, idx) => idx !== i));
  return (
    <div className="field">
      <div className="field-label">{label} ({photos.length}){compressing && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--attention-texte)' }}>⏳ Compression…</span>}</div>
      <div className="photo-grid">
        {photos.map((p, i) => (
          <div key={i} style={{ position: 'relative' }}>
            <img src={p} alt="" className="photo-thumb" />
            <div onClick={() => remove(i)} style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.6)', color: '#fff', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, cursor: 'pointer' }}>✕</div>
          </div>
        ))}
        {(max === 0 || photos.length < max) && !compressing && (
          <>
            <label className="photo-add" style={{ flexDirection: 'column', gap: 3 }}>
              <span style={{ fontSize: 22 }}>📷</span>
              <span style={{ fontSize: 10, color: 'var(--texte-doux)' }}>Photo</span>
              <input type="file" accept="image/*" capture="environment" onChange={handleAdd} style={{ display: 'none' }} />
            </label>
            <label className="photo-add" style={{ flexDirection: 'column', gap: 3 }}>
              <span style={{ fontSize: 22 }}>🖼️</span>
              <span style={{ fontSize: 10, color: 'var(--texte-doux)' }}>Galerie</span>
              <input type="file" accept="image/*" onChange={handleAdd} style={{ display: 'none' }} multiple />
            </label>
          </>
        )}
      </div>
    </div>
  );
});

function Toggle({ label, value, onChange, verrouille = false, note = '' }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '0.5px solid var(--bord)', gap: 10 }}>
      <span style={{ fontSize: 14, color: 'var(--texte)' }}>
        {label}
        {note && <span style={{ display: 'block', fontSize: 12, color: 'var(--attention-texte)', marginTop: 2 }}>{note}</span>}
      </span>
      <div onClick={() => { if (!verrouille) onChange(!value); }} style={{ width: 44, height: 26, borderRadius: 13, background: value ? 'var(--accent)' : 'var(--bord-fort)', position: 'relative', cursor: verrouille ? 'not-allowed' : 'pointer', opacity: verrouille ? 0.7 : 1, transition: 'background .2s', flexShrink: 0 }}>
        <div style={{ position: 'absolute', top: 3, left: value ? 21 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left .2s' }} />
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = 'text', placeholder = '', obligatoire = false }) {
  const manquant = obligatoire && String(value ?? '').trim() === '';
  return (
    <div className="field">
      <div className="field-label">{label}{obligatoire && <span style={{ color: 'var(--danger)' }}> *</span>}</div>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width: '100%', padding: '10px 12px', border: `${manquant ? '1px' : '0.5px'} solid ${manquant ? 'var(--danger)' : 'var(--bord-fort)'}`, borderRadius: 10, fontSize: 14, background: 'var(--surface-2)', color: 'var(--texte)' }} />
    </div>
  );
}

function SectionTitle({ children, color = '#185FA5' }) {
  return (
    <div style={{ background: color + '12', borderLeft: `4px solid ${color}`, borderRight: `4px solid ${color}`, borderRadius: 6, padding: '9px 16px', fontSize: 14, fontWeight: 700, color: 'var(--texte)', margin: '20px 0 12px', textAlign: 'center' }}>
      {children}
    </div>
  );
}

function MultiSelect({ label, options, selected, onChange }) {
  const toggle = opt => {
    if (selected.includes(opt)) onChange(selected.filter(s => s !== opt));
    else onChange([...selected, opt]);
  };
  return (
    <div className="field">
      <div className="field-label">{label}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
        {options.map(opt => (
          <div key={opt} onClick={() => toggle(opt)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, border: `1.5px solid ${selected.includes(opt) ? 'var(--accent)' : 'var(--bord-fort)'}`, background: selected.includes(opt) ? 'var(--info-fond)' : 'var(--surface-2)', cursor: 'pointer' }}>
            <div style={{ width: 20, height: 20, borderRadius: 4, border: `2px solid ${selected.includes(opt) ? 'var(--accent)' : 'var(--bord-fort)'}`, background: selected.includes(opt) ? 'var(--accent)' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {selected.includes(opt) && <span style={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>✓</span>}
            </div>
            <span style={{ fontSize: 14, color: selected.includes(opt) ? 'var(--info-texte)' : 'var(--texte)', fontWeight: selected.includes(opt) ? 600 : 400 }}>{opt}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function InfoCard({ children }) {
  return <div style={{ background: 'var(--surface-3)', borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 13 }}>{children}</div>;
}

export default function CompteRenduSAV() {
  const { id } = useParams();
  const navigate = useNavigate();
  const sigRef = useRef(null);

  const [dossier, setDossier] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState('');
  const [pdfUrl, setPdfUrl] = useState('');
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [offlineRestored, setOfflineRestored] = useState(false);

  // Résultat
  const [resultat, setResultat] = useState('CLEARED');

  // CLEARED
  const [typesReparation, setTypesReparation] = useState([]);
  const [constructeurOnt, setConstructeurOnt] = useState('');
  const [numSerieOnt, setNumSerieOnt] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const [photosFacade, setPhotosFacade] = useState([]);
  const [photosAvantPanne, setPhotosAvantPanne] = useState([]);
  const [photoPTO, setPhotoPTO] = useState([]);
  const [photoValeurPTO, setPhotoValeurPTO] = useState([]);
  const [photosApresVoyantONT, setPhotosApresVoyantONT] = useState([]);
  const [photosAutres, setPhotosAutres] = useState([]);

  // FAILED
  const [typeEchec, setTypeEchec] = useState([]);
  const [devis, setDevis] = useState(false);
  const [photosPanne, setPhotosPanne] = useState([]);
  const [photosCheminementDevis, setPhotosCheminementDevis] = useState([]);

  // FAILED devis — champs supplémentaires
  const [typeRaccSAV, setTypeRaccSAV] = useState('RAC_I');
  const [lgPublic, setLgPublic] = useState('');
  const [lgPrive, setLgPrive] = useState('');

  const [travGrandeHauteur, setTravGrandeHauteur] = useState(false);
  // Les trois prestations que le devis chiffre et que ce formulaire ne
  // demandait pas : la conduite d'activite devait les ajouter a la main.
  const [goulotte, setGoulotte] = useState(false);
  const [lgGoulotte, setLgGoulotte] = useState('');
  const [percementVertical, setPercementVertical] = useState(false);
  const [nbPercementsVerticaux, setNbPercementsVerticaux] = useState('');
  const [photosPercementVertical, setPhotosPercementVertical] = useState([]);
  const [hydrocurage, setHydrocurage] = useState(false);
  const [hno, setHno] = useState(false);
  const [binome, setBinome] = useState(false);
  const [dureeEstimee, setDureeEstimee] = useState('');
  const [percementComplexe, setPercementComplexe] = useState(false);

  // Clôture commun
  const [commentaireTechnicien, setCommentaireTechnicien] = useState('');
  const [dechargeKosc, setDechargeKosc] = useState('');
  const [nomContact, setNomContact] = useState('');
  const [dateArrivee, setDateArrivee] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [signatureSite, setSignatureSite] = useState('');
  const [signedSite, setSignedSite] = useState(false);

  const lgTotal = (parseFloat(lgPublic) || 0) + (parseFloat(lgPrive) || 0);

  // « RACC suite SAV (RAC_I) » et consorts sont de vrais raccordements : eux
  // seuls peuvent donner un forfait plus-value, et seulement si la longueur
  // depasse le seuil. Un echec facture au devis n'en donne jamais, le devis
  // chiffrant deja la longueur.
  const typoDossier = (dossier && dossier.typo_raccordement) || '';
  const estRaccSuiteSav = /RACCs+suites+SAV/i.test(typoDossier);
  const seuilFpv = estRaccSuiteSav ? SEUILS_FPV[familleFpv(typoDossier)] : undefined;
  const fpv = seuilFpv !== undefined && lgTotal > seuilFpv;
  const isCleared = resultat === 'CLEARED';
  const isFailed = resultat === 'FAILED';
  const devisImpose = isFailed && typeEchec.includes(ECHEC_DEVIS_OBLIGATOIRE);

  // Le motif coche le devis de lui-même, y compris sur un brouillon rouvert.
  useEffect(() => { if (devisImpose && !devis) setDevis(true); }, [devisImpose, devis]);

  const LOCAL_KEY = `cr_draft_${id}`;

  const saveLocalDraft = (payload, doss) => {
    saveDraft(LOCAL_KEY, payload, doss).catch(() => {});
  };

  const applyDraftData = (cr) => {
    if (cr.resultat) setResultat(cr.resultat);
    if (cr.types_reparation) setTypesReparation(cr.types_reparation);
    if (cr.constructeur_ont) setConstructeurOnt(cr.constructeur_ont);
    if (cr.numero_serie_ont) setNumSerieOnt(cr.numero_serie_ont);
    if (cr.photos_facade) setPhotosFacade(cr.photos_facade);
    if (cr.photos_avant_panne) setPhotosAvantPanne(cr.photos_avant_panne);
    if (cr.photo_pto) setPhotoPTO(cr.photo_pto);
    if (cr.photo_valeur_pto) setPhotoValeurPTO(cr.photo_valeur_pto);
    if (cr.photos_apres_voyant_ont) setPhotosApresVoyantONT(cr.photos_apres_voyant_ont);
    if (cr.photos_autres) setPhotosAutres(cr.photos_autres);
    if (cr.type_echec_sav) setTypeEchec(cr.type_echec_sav);
    if (cr.devis !== undefined) setDevis(cr.devis);
    if (cr.type_raccordement_sav) setTypeRaccSAV(cr.type_raccordement_sav);
    if (cr.photos_panne) setPhotosPanne(cr.photos_panne);
    if (cr.photos_cheminement_devis) setPhotosCheminementDevis(cr.photos_cheminement_devis);
    if (cr.lg_public) setLgPublic(cr.lg_public);
    if (cr.lg_prive) setLgPrive(cr.lg_prive);
    if (cr.trav_grande_hauteur !== undefined) setTravGrandeHauteur(cr.trav_grande_hauteur);
    if (cr.goulotte !== undefined) setGoulotte(cr.goulotte);
    if (cr.lg_goulotte) setLgGoulotte(cr.lg_goulotte);
    if (cr.percement_vertical !== undefined) setPercementVertical(cr.percement_vertical);
    if (cr.nb_percements_verticaux) setNbPercementsVerticaux(cr.nb_percements_verticaux);
    if (cr.photos_percement_vertical?.length) setPhotosPercementVertical(cr.photos_percement_vertical);
    if (cr.hydrocurage !== undefined) setHydrocurage(cr.hydrocurage);
    if (cr.hno !== undefined) setHno(cr.hno);
    if (cr.binome !== undefined) setBinome(cr.binome);
    if (cr.duree_estimee) setDureeEstimee(cr.duree_estimee);
    if (cr.percement_complexe !== undefined) setPercementComplexe(cr.percement_complexe);
    if (cr.commentaire_technicien) setCommentaireTechnicien(cr.commentaire_technicien);
    if (cr.decharge_kosc) setDechargeKosc(cr.decharge_kosc);
    if (cr.nom_contact) setNomContact(cr.nom_contact);
    if (cr.date_arrivee) setDateArrivee(cr.date_arrivee);
    if (cr.date_fin) setDateFin(cr.date_fin);
    if (cr.signature_site) { setSignatureSite(cr.signature_site); setSignedSite(true); }
    if (cr.pdf_url) setPdfUrl(cr.pdf_url);
  };

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      const localDraft = await loadDraft(LOCAL_KEY).catch(() => null);
      try {
        const [dData, crData] = await Promise.all([getDossier(id), getCR(id)]);
        setDossier(dData.dossier);
        // Le bandeau horodate des photos s'en sert quand le GPS ne repond pas,
        // et la position retenue du dossier precedent ne vaut plus rien ici.
        oublierLieu();
        poserAdresseCommande(dData.dossier
          ? [dData.dossier.numero, dData.dossier.rue, dData.dossier.code_postal, dData.dossier.ville]
              .filter(Boolean).join(' ')
          : '');
        const cr = crData.cr;
        const localTs = localDraft?._ts || 0;
        const serverTs = cr?._ts || 0;
        if (localTs > serverTs + 60000 && localDraft?.cr) {
          applyDraftData(localDraft.cr);
          setOfflineRestored(true);
        } else if (cr) {
          applyDraftData(cr);
          saveLocalDraft(cr, null);
        } else if (localDraft?.cr) {
          applyDraftData(localDraft.cr);
          setOfflineRestored(true);
        }
      } catch {
        if (localDraft) {
          if (localDraft.dossier) setDossier(localDraft.dossier);
          if (localDraft.cr) applyDraftData(localDraft.cr);
          setOfflineRestored(true);
        }
      }
    };
    load();
  }, [id]);

  // Détection connexion
  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  const captureSignature = () => {
    if (sigRef.current && !sigRef.current.isEmpty()) {
      setSignatureSite(sigRef.current.toDataURL('image/png'));
      setSignedSite(true);
    }
  };

  const buildPayload = (final) => ({
    dossier_id: id,
    final,
    type_cr: 'SAV',
    resultat,
    types_reparation: typesReparation,
    constructeur_ont: constructeurOnt,
    numero_serie_ont: numSerieOnt,
    type_echec_sav: typeEchec,
    devis,
    type_raccordement_sav: typeRaccSAV,
    lg_public: lgPublic,
    lg_prive: lgPrive,
    lg_total: lgTotal,
    fpv,
    trav_grande_hauteur: travGrandeHauteur,
    goulotte, lg_goulotte: lgGoulotte,
    percement_vertical: percementVertical,
    nb_percements_verticaux: nbPercementsVerticaux,
    photos_percement_vertical: photosPercementVertical,
    hydrocurage,
    hno,
    binome,
    duree_estimee: dureeEstimee,
    percement_complexe: percementComplexe,
    commentaire_technicien: commentaireTechnicien,
    decharge_kosc: dechargeKosc,
    nom_contact: nomContact,
    date_arrivee: dateArrivee,
    date_fin: dateFin,
    signature_site: signatureSite,
    photos_facade: photosFacade,
    photos_avant_panne: photosAvantPanne,
    photo_pto: photoPTO,
    photo_valeur_pto: photoValeurPTO,
    photos_apres_voyant_ont: photosApresVoyantONT,
    photos_autres: photosAutres,
    photos_panne: photosPanne,
    photos_cheminement_devis: photosCheminementDevis,
  });

  // Auto-save quand l'app passe en arrière-plan
  const autoSaveRef = useRef(null);
  autoSaveRef.current = { dossier, generating, pdfUrl, buildPayload, saveLocalDraft, LOCAL_KEY };
  useEffect(() => {
    const handleHide = () => {
      if (!document.hidden) return;
      const s = autoSaveRef.current;
      if (s && s.dossier && !s.generating && !s.pdfUrl) {
        const payload = s.buildPayload(false);
        saveDraftSync(s.LOCAL_KEY, payload, s.dossier);
        s.saveLocalDraft(payload, s.dossier);
        try { saveCR(payload); } catch {}
      }
    };
    document.addEventListener('visibilitychange', handleHide);
    return () => document.removeEventListener('visibilitychange', handleHide);
  }, []);

  // Sauvegarde locale périodique toutes les 30s
  useEffect(() => {
    const timer = setInterval(() => {
      const s = autoSaveRef.current;
      if (s && s.dossier && !s.generating && !s.pdfUrl) {
        s.saveLocalDraft(s.buildPayload(false), s.dossier);
      }
    }, 30000);
    return () => clearInterval(timer);
  }, []);

  const handleSaveDraft = async () => {
    setSaving(true); setMessage('');
    const payload = buildPayload(false);
    const localOk = await saveDraft(LOCAL_KEY, payload, dossier).catch(() => false);
    const localMsg = localOk === true    ? '✓ Sauvegardé localement (photos incluses)'
                   : localOk === 'fallback' ? '⚠ Sauvegardé sans photos (iPhone — stockage limité). Revenez vite !'
                   : '⚠ Erreur sauvegarde locale — vérifiez l\'espace disponible';
    if (!isOnline) {
      setSaving(false);
      setMessage(localMsg);
      setSaving(false);
      return;
    }
    try { await saveCR(payload); setMessage(localOk ? '✓ Brouillon sauvegardé (local + serveur)' : '✓ Brouillon sauvegardé sur le serveur'); }
    catch { setMessage(localOk ? '✓ Sauvegardé localement — erreur serveur' : 'Erreur lors de la sauvegarde'); }
    finally { setSaving(false); }
  };

  const handleBack = async () => {
    if (dossier && !generating && !pdfUrl) {
      const payload = buildPayload(false);
      saveLocalDraft(payload, dossier);
      try { await saveCR(payload); } catch {}
    }
    navigate(`/dossier/${id}`);
  };

  const handleGenerate = async () => {
    if (!isOnline) {
      setMessage('Impossible de générer le PDF hors ligne. Reconnectez-vous puis réessayez.');
      return;
    }
    if (!dechargeKosc.trim()) {
      setMessage('Le N° de décharge FRONT COVAGE est obligatoire');
      return;
    }
    if (devisImpose && (String(lgPublic).trim() === '' || String(lgPrive).trim() === '')) {
      setMessage('Échec « ' + ECHEC_DEVIS_OBLIGATOIRE + ' » : renseignez les longueurs de câble domaine public et domaine privé (0 si aucune).');
      return;
    }
    setGenerating(true); setMessage('');
    const charge = buildPayload(true);
    try {
      const data = await saveCR(charge);
      setPdfUrl(data.pdf_url);
      setMessage('CRI SAV généré avec succès !');
      removeDraft(LOCAL_KEY).catch(() => {});
    } catch (e) { setMessage(messageEnvoi(e, poidsApprox(charge), 'Erreur lors de la génération du CRI SAV')); }
    finally { setGenerating(false); }
  };

  if (!dossier) return (
    <div className="app-shell">
      <div className="topbar"><div className="topbar-title">Chargement…</div></div>
    </div>
  );

  return (
    <div className="app-shell">
      <div className="topbar">
        <div className="back-btn" onClick={handleBack}>‹</div>
        <div className="topbar-title">CRI SAV</div>
        <div style={{ fontSize: 11, color: 'var(--texte-doux)' }}>{dossier.ref_commande}</div>
      </div>

      {!isOnline && (
        <div style={{ background: 'var(--attention-fond)', color: 'var(--attention-texte)', padding: '8px 16px', fontSize: 13, fontWeight: 600, textAlign: 'center', borderBottom: '1px solid var(--attention-bord)' }}>
          Hors ligne — données sauvegardées localement
        </div>
      )}
      {offlineRestored && (
        <div style={{ background: 'var(--succes-fond)', color: 'var(--succes-texte)', padding: '8px 16px', fontSize: 13, fontWeight: 600, textAlign: 'center', borderBottom: '1px solid var(--succes-bord)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Données restaurées depuis la sauvegarde locale</span>
          <span style={{ cursor: 'pointer', fontWeight: 400 }} onClick={() => setOfflineRestored(false)}>✕</span>
        </div>
      )}

      <div className="content">

        {/* Photo façade */}
        <SectionTitle>Photo façade du site</SectionTitle>
        <PhotoSection label="Photo façade (obligatoire)" photos={photosFacade} onChange={setPhotosFacade} max={3} />

        {/* Résultat */}
        <SectionTitle>Résultat du SAV</SectionTitle>
        <div className="field">
          <div style={{ display: 'flex', gap: 8 }}>
            {['CLEARED', 'FAILED'].map(r => (
              <div key={r} onClick={() => setResultat(r)} style={{ flex: 1, padding: '12px 8px', textAlign: 'center', borderRadius: 10, border: `2px solid ${resultat === r ? (r === 'CLEARED' ? 'var(--succes-bord)' : 'var(--danger)') : 'var(--bord-fort)'}`, background: resultat === r ? (r === 'CLEARED' ? 'var(--succes-fond)' : 'var(--danger-fond)') : 'var(--surface-2)', color: resultat === r ? (r === 'CLEARED' ? 'var(--succes-texte)' : 'var(--danger-texte)') : 'var(--texte-doux)', fontWeight: 700, cursor: 'pointer', fontSize: 16 }}>{r}</div>
            ))}
          </div>
        </div>

        {/* ROP SAV affiché en lecture seule */}
        {dossier.rop_sav && (
          <>
            <SectionTitle>ROP SAV</SectionTitle>
            <div style={{ background: 'var(--surface-3)', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
              {[
                ['Référence prise', dossier.rop_sav.ref_prise],
                ['Ref PT PBO', dossier.rop_sav.ref_pbo],
                ['Adresse PBO', dossier.rop_sav.adresse_pbo],
                ['Tube', dossier.rop_sav.tube],
                ['Fibre', dossier.rop_sav.fibre],
                ['Ref PT PM', dossier.rop_sav.ref_pm],
                ['Adresse PM', dossier.rop_sav.adresse_pm],
                ['Position opérateur (porte gauche)', dossier.rop_sav.position_porte_gauche],
                ['Position client (porte droite)', dossier.rop_sav.position_porte_droite],
                ['SLID de l\'ONT', (() => {
                  const m = (dossier.commentaire_valea || '').match(/SLID de l['']ONT\s*:\s*(.+?)(?:\n|$)/i);
                  return m ? m[1].trim() : null;
                })()],
              ].filter(([, v]) => v).map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '0.5px solid var(--bord)', fontSize: 13 }}>
                  <span style={{ color: 'var(--texte-doux)', flexShrink: 0, maxWidth: '45%' }}>{k}</span>
                  <span style={{ fontWeight: 500, textAlign: 'right', marginLeft: 8 }}>{v}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ===== CLEARED ===== */}
        {isCleared && (
          <>
            <SectionTitle>Résolution SAV — CLEARED</SectionTitle>
            <MultiSelect label="Type(s) de réparation effectuée(s)" options={TYPES_REPARATION} selected={typesReparation} onChange={setTypesReparation} />

            {/* ONT — affiché uniquement si "Changement ONT" est coché */}
            {typesReparation.includes('Changement ONT') && (
              <>
                <SectionTitle>ONT — Optical Network Terminal</SectionTitle>
                <div className="field">
                  <div className="field-label">Constructeur ONT</div>
                  <input
                    type="text"
                    value={constructeurOnt}
                    onChange={e => setConstructeurOnt(e.target.value)}
                    placeholder="Ex: Nokia, Huawei..."
                    style={{ width: '100%', padding: '10px 12px', border: '0.5px solid var(--bord-fort)', borderRadius: 10, fontSize: 14, background: 'var(--surface-2)', color: 'var(--texte)' }}
                  />
                </div>
                <div className="field">
                  <div className="field-label">N° de série ONT</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      type="text"
                      value={numSerieOnt}
                      onChange={e => setNumSerieOnt(e.target.value)}
                      placeholder="Ex: ALCLFD574109"
                      autoCapitalize="characters"
                      style={{ flex: 1, padding: '10px 12px', border: '0.5px solid var(--bord-fort)', borderRadius: 10, fontSize: 14, background: 'var(--surface-2)', color: 'var(--texte)' }}
                    />
                    <button
                      onClick={() => setShowScanner(true)}
                      style={{ padding: '10px 14px', background: 'var(--accent-plein)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                    >
                      📷 Scanner
                    </button>
                  </div>
                  {numSerieOnt && (
                    <div style={{ marginTop: 6, fontSize: 12, color: 'var(--succes-texte)', background: 'var(--succes-fond)', padding: '4px 10px', borderRadius: 6 }}>
                      ✓ {numSerieOnt}
                    </div>
                  )}
                </div>
                {showScanner && (
                  <BarcodeScanner
                    onDetected={val => { setNumSerieOnt(val); setShowScanner(false); }}
                    onClose={() => setShowScanner(false)}
                  />
                )}
              </>
            )}

            <PhotoSection label="Photos AVANT intervention (état de panne)" photos={photosAvantPanne} onChange={setPhotosAvantPanne} />
            <PhotoSection label="Photo PTO" photos={photoPTO} onChange={setPhotoPTO} max={3} />
            <PhotoSection label="Photo valeur photomètre PTO" photos={photoValeurPTO} onChange={setPhotoValeurPTO} max={3} />
            <PhotoSection label="Photos APRÈS intervention — voyant ONT OK" photos={photosApresVoyantONT} onChange={setPhotosApresVoyantONT} />
            <PhotoSection label="Photos autres (optionnel)" photos={photosAutres} onChange={setPhotosAutres} />
          </>
        )}

        {/* ===== FAILED ===== */}
        {isFailed && (
          <>
            <SectionTitle color="#D42B2B">Cause de l'échec SAV</SectionTitle>
            <MultiSelect label="Type(s) d'échec" options={TYPES_ECHEC_SAV} selected={typeEchec} onChange={setTypeEchec} />
            <PhotoSection label="Photos de la panne" photos={photosPanne} onChange={setPhotosPanne} />

            <div style={{ marginTop: 8 }}>
              <Toggle label="Devis à réaliser" value={devis} onChange={setDevis}
                verrouille={devisImpose}
                note={devisImpose ? 'Imposé par « ' + ECHEC_DEVIS_OBLIGATOIRE + ' »' : ''} />
            </div>

            {devis && (
              <>
                <SectionTitle color="#854F0B">Détails devis</SectionTitle>

                <div className="field">
                  <div className="field-label">Type de raccordement à réaliser</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {TYPES_RACCORDEMENT.map(t => (
                      <div key={t} onClick={() => setTypeRaccSAV(t)} style={{ padding: '8px 14px', borderRadius: 20, border: `1.5px solid ${typeRaccSAV === t ? 'var(--attention-texte)' : 'var(--bord-fort)'}`, background: typeRaccSAV === t ? 'var(--attention-fond)' : 'var(--surface-2)', color: typeRaccSAV === t ? 'var(--attention-texte)' : 'var(--texte-doux)', fontWeight: typeRaccSAV === t ? 700 : 400, cursor: 'pointer', fontSize: 13 }}>{t}</div>
                    ))}
                  </div>
                </div>

                <Input label="Longueur câble domaine public (ml)" value={lgPublic} onChange={setLgPublic} type="number" obligatoire={devisImpose} />
                <Input label="Longueur câble domaine privé (ml)" value={lgPrive} onChange={setLgPrive} type="number" obligatoire={devisImpose} />
                <InfoCard><strong>Longueur totale calculée : {lgTotal} ml</strong></InfoCard>

                {/* Le FPV n'a pas sa place ici : sur un echec facture au devis,
                    le devis chiffre deja la longueur. Il ne vaut que pour un
                    raccordement suite SAV, ou il se calcule tout seul. */}
                <Toggle label="Travaux grande hauteur besoin Nacelle intérieure" value={travGrandeHauteur} onChange={setTravGrandeHauteur} />
                <Toggle label="Hydrocurage / débouchage de fourreau" value={hydrocurage} onChange={setHydrocurage} />
                <Toggle label="Goulotte posée" value={goulotte} onChange={setGoulotte} />
                {goulotte && <Input label="Longueur goulotte (ml)" value={lgGoulotte} onChange={setLgGoulotte} type="number" />}
                <Toggle label="Percement vertical / carottage" value={percementVertical} onChange={setPercementVertical} />
                {percementVertical && (
                  <>
                    <Input label="Nombre de percements verticaux / carottages" value={nbPercementsVerticaux} onChange={setNbPercementsVerticaux} type="number" />
                    <PhotoSection label="Photos percement vertical / carottage" photos={photosPercementVertical} onChange={setPhotosPercementVertical} max={0} />
                  </>
                )}
                <Toggle label="Intervention de nuit (HNO)" value={hno} onChange={setHno} />
                <Toggle label="Binôme nécessaire" value={binome} onChange={setBinome} />

                <Input label="Nombre d'heures nécessaires à l'intervention" value={dureeEstimee} onChange={setDureeEstimee} type="number" placeholder="Ex: 3" />

                <Toggle label="Percement complexe" value={percementComplexe} onChange={setPercementComplexe} />

                <SectionTitle color="#854F0B">Photos cheminement câble PTO → PBO</SectionTitle>
                <PhotoSection label="Photos cheminement (sans limite)" photos={photosCheminementDevis} onChange={setPhotosCheminementDevis} />
              </>
            )}
          </>
        )}

        {/* ===== CLOTURE commune ===== */}
        <SectionTitle>Clôture de l'intervention</SectionTitle>

        <div className="field">
          <div className="field-label">Date et heure d'arrivée sur site</div>
          <input type="datetime-local" value={dateArrivee} onChange={e => setDateArrivee(e.target.value)} style={{ width: '100%', padding: '10px 12px', border: '0.5px solid var(--bord-fort)', borderRadius: 10, fontSize: 14, background: 'var(--surface-2)', color: 'var(--texte)' }} />
        </div>
        <div className="field">
          <div className="field-label">Date et heure de fin d'intervention</div>
          <input type="datetime-local" value={dateFin} onChange={e => setDateFin(e.target.value)} style={{ width: '100%', padding: '10px 12px', border: '0.5px solid var(--bord-fort)', borderRadius: 10, fontSize: 14, background: 'var(--surface-2)', color: 'var(--texte)' }} />
        </div>

        <div className="field">
          <div className="field-label">Remarques / Travaux réalisés</div>
          <textarea value={commentaireTechnicien} onChange={e => setCommentaireTechnicien(e.target.value)} placeholder={isCleared ? 'Décrire les travaux réalisés…' : 'Décrire la raison de l\'échec…'} style={{ width: '100%', padding: '10px 12px', border: '0.5px solid var(--bord-fort)', borderRadius: 10, fontSize: 14, background: 'var(--surface-2)', color: 'var(--texte)', minHeight: 90, resize: 'vertical', fontFamily: 'inherit' }} />
        </div>

        <div style={{ background: 'var(--attention-fond)', border: `1.5px solid ${dechargeKosc ? 'var(--succes-bord)' : 'var(--attention-bord)'}`, borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--attention-texte)', marginBottom: 6 }}>⚠️ Décharge FRONT COVAGE <span style={{ color: 'var(--danger)' }}>*</span></div>
          <div style={{ fontSize: 12, color: 'var(--attention-texte)', marginBottom: 10 }}>L'appel au front COVAGE (01 86 95 14 89) est indispensable. Une décharge vous sera délivrée.</div>
          <input type="text" value={dechargeKosc} onChange={e => setDechargeKosc(e.target.value)} placeholder="Ex: D260401_15424" style={{ width: '100%', padding: '10px 12px', border: `0.5px solid ${dechargeKosc ? 'var(--succes-bord)' : 'var(--attention-bord)'}`, borderRadius: 10, fontSize: 14, background: '#fff', color: 'var(--texte)' }} />
          {!dechargeKosc && <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 4 }}>Obligatoire pour générer le CR</div>}
        </div>

        <div className="field">
          <div className="field-label">Nom du client / contact sur site</div>
          <input type="text" value={nomContact} onChange={e => setNomContact(e.target.value)} style={{ width: '100%', padding: '10px 12px', border: '0.5px solid var(--bord-fort)', borderRadius: 10, fontSize: 14, background: 'var(--surface-2)', color: 'var(--texte)' }} />
        </div>

        <div className="field">
          <div className="field-label">Signature client</div>
          {signedSite ? (
            <div>
              <div style={{ border: '0.5px solid var(--succes-bord)', borderRadius: 10, background: 'var(--succes-fond)', padding: 8 }}>
                <img src={signatureSite} alt="signature" style={{ maxHeight: 80 }} />
              </div>
              <div style={{ fontSize: 13, color: 'var(--accent)', cursor: 'pointer', textAlign: 'right', padding: '4px 0' }} onClick={() => { sigRef.current?.clear(); setSignatureSite(''); setSignedSite(false); }}>
                Effacer et recommencer
              </div>
            </div>
          ) : (
            <div>
              <div className="sign-canvas-wrap">
                <SignatureCanvas ref={sigRef} canvasProps={{ width: 340, height: 120, style: { display: 'block', width: '100%', height: 120 } }} onEnd={captureSignature} />
              </div>
              <div style={{ fontSize: 13, color: 'var(--accent)', cursor: 'pointer', textAlign: 'right', padding: '4px 0' }} onClick={() => sigRef.current?.clear()}>Effacer</div>
            </div>
          )}
        </div>

        {message && (
          <div style={{ padding: '10px 12px', borderRadius: 10, background: message.includes('succès') || message.includes('sauvegardé') ? 'var(--succes-fond)' : 'var(--danger-fond)', color: message.includes('succès') || message.includes('sauvegardé') ? 'var(--succes-texte)' : 'var(--danger-texte)', fontSize: 13, marginBottom: 10 }}>
            {message}
          </div>
        )}

        {pdfUrl && (
          <button onClick={() => {
            const token = localStorage.getItem('token');
            fetch(pdfUrl, { headers: { Authorization: `Bearer ${token}` } })
              .then(r => r.blob())
              .then(blob => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = pdfUrl.split('/').pop() || 'CR_SAV.pdf';
                a.click();
                URL.revokeObjectURL(url);
              });
          }} style={{ display: 'block', width: '100%', textAlign: 'center', padding: 12, borderRadius: 12, background: 'var(--surface-3)', color: 'var(--texte)', border: '0.5px solid var(--bord-fort)', fontWeight: 600, marginBottom: 8, fontSize: 15, cursor: 'pointer' }}>
            Télécharger le CRI SAV
          </button>
        )}

        <button className="btn btn-primary" style={{ background: 'var(--attention-texte)' }} onClick={handleGenerate} disabled={generating}>
          {generating ? 'Génération PDF…' : `Générer CRI SAV ${resultat}`}
        </button>
        <button className="btn btn-secondary" onClick={handleSaveDraft} disabled={saving}>
          {saving ? 'Sauvegarde…' : 'Sauvegarder brouillon'}
        </button>
        <div style={{ height: 16 }} />
      </div>

      <div className="bottomnav">
        <div className="nav-item" onClick={() => navigate('/planning')}><span className="nav-icon">📅</span>Planning</div>
        <div className="nav-item" onClick={handleBack}><span className="nav-icon">📁</span>Dossier</div>
        <div className="nav-item active"><span className="nav-icon">📝</span>CR SAV</div>
      </div>
    </div>
  );
}
